import React, { useMemo, useState, useCallback } from 'react';
import { Box, Typography, Chip, Avatar } from '@mui/material';
import { LineBreaker } from '@/atomicComponent/AtomicComponents';
import ApprovalFilters from './components/ApprovalFilters';
import { useFetchAllApprovalRequests } from '@/data/swrs/trc';
import { useFormSubmit } from '@/hook/useFormSubmit';
import { handleErrorResponse } from '@/utils/handleErrorResponse';
import { respondToTrcRequest } from '@/data/api/trc';
import { ACTION_BUTTONS, STATUS } from './utils/constants';
import GenericListView from '@/AdminComponent/GenericListView';
import { RequestInfoModal } from '@/Pages/admin/TeacherResourceCenter/components/RequestInfoModal';
import { getEmailSentDate } from '@/Pages/admin/TeacherResourceCenter/components/utils/getEmailSentDate';

const normalizeStatus = (s) => String(s ?? '').trim().toLowerCase();
const getRawStatus = (req) => req?.access_status ?? req?.accessStatus ?? 'N/A';
const formatDate = (d) => {
  const dt = d ? new Date(d) : null;
  if (!dt || isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const Approval = () => {
  const [filters, setFilters] = useState({ role: '', status: '', search: '', sort: '' });
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);

  const { data: approvalRequests, error, isLoading, mutate } = useFetchAllApprovalRequests(filters);

  const handleTrcRequests = useFormSubmit(async ({ userId, status }, token) => {
    const res = await respondToTrcRequest({ userId, status }, token);
    if (handleErrorResponse(res)) return;
    mutate();
  });

  const handleInfoClick = (req) => {
    setSelectedReq(req)
    setShowInfoModal(true)
  }

const renderButtons = useCallback(
  (req) => {
    const s = normalizeStatus(getRawStatus(req));
    if (s !== STATUS.PENDING) return [];

    return ACTION_BUTTONS.map((btn) => ({
      ...btn,
      onClick: () =>
        btn.status === STATUS.GET_INFO
          ? handleInfoClick(req) 
          : handleTrcRequests({ userId: req?._id, status: btn.status }),
    }));
  },
  [handleTrcRequests, handleInfoClick]
);

const renderInfoElement = useCallback((request) => {
    const rawStatus = getRawStatus(request);
    const s = normalizeStatus(rawStatus);
    const emailSentDate = getEmailSentDate(request);

    const statusColor =
      s === STATUS.APPROVED ? 'success'
      : s === STATUS.REJECTED ? 'error'
      : 'default';

    const firstLetter = (request?.name || '').trim().charAt(0).toUpperCase();

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>{firstLetter}</Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {request?.name || '—'}
            </Typography>
            <Chip label={rawStatus} color={statusColor} size="small" />
          </Box>
        </Box>

        {request?.email && (
          <Typography variant="body1" color="text.secondary">
            Email : <a href={`mailto:${request.email}`}>{request.email}</a>
          </Typography>
        )}
        <Typography variant="body1" color="text.secondary">
          Role : {request?.role || '—'}
        </Typography>

        {!!request?.link && (
          <Typography variant="body1">
            <a href={request.link} target="_blank" rel="noopener noreferrer">
              View Link - {request.link}
            </a>
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Requested: {formatDate(request?.createdAt)} | Updated: {formatDate(request?.updatedAt)}
          {emailSentDate && (
            <span> | Email Sent: {formatDate(emailSentDate)}</span>
          )}
        </Typography>
        <LineBreaker />
      </Box>
    );
  }, []);

  const filterSlot = useMemo(
    () => <ApprovalFilters filters={filters} setFilters={setFilters} />,
    [filters]
  );

  return (
    <>
    <GenericListView
      title="TRC Approval Requests"
      filterSlot={filterSlot}
      items={approvalRequests?.data ?? []}
      error={error}
      isLoading={isLoading && !error}
      renderButtons={renderButtons}
      renderInfoElement={renderInfoElement}
      gridItem={{ xs: 12, md: 6 }}
    />
    {showInfoModal && (
      <RequestInfoModal 
        req={selectedReq} 
        onClose={() => setShowInfoModal(false)} 
        onEmailSent={() => mutate()}
      />
    )}
    </>
  );
};

export default Approval;
